import React from 'react';
import Box from '@mui/material/Box';
import { indexBy } from 'ramda';
import { MODEL_DRAW_WIDTH } from 'config';
import { useMarquee } from '../../../contexts/MarqueeContext';
import { Model } from '../../../hooks/useModelState';
import { visibleNetwork } from '../../../MineModel/levelAreaFilter';
import useSceneManager from '../../../hooks/SceneManagerProvider/useSceneManager';

interface MarqueeOverlayProps {
  model: Model;
}

const MarqueeOverlay = ({ model }: MarqueeOverlayProps) => {
  const marquee = useMarquee();
  const sceneManager = useSceneManager();
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const isDraggingRef = React.useRef(false);

  React.useEffect(() => {
    canvasRef.current = document.querySelector('canvas');
  }, []);

  const handleMouseDown = React.useCallback((e: React.MouseEvent) => {
    if (!marquee.isMarqueeMode || e.button !== 0) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const drawerOpen = sceneManager?.state.drawerOpen ?? false;
    const drawerWidth = drawerOpen ? MODEL_DRAW_WIDTH : 0;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (x >= 0 && x <= rect.width - drawerWidth && y >= 0 && y <= rect.height) {
      isDraggingRef.current = true;
      e.preventDefault();
      e.stopPropagation();
      marquee.handleMarqueeStart(x, y);
    }
  }, [marquee, sceneManager]);

  const handleMouseMove = React.useCallback((e: React.MouseEvent) => {
    if (!marquee.isMarqueeMode || !isDraggingRef.current) return;
    if (!marquee.marqueeBox) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    e.preventDefault();
    e.stopPropagation();
    marquee.handleMarqueeMove(x, y);
  }, [marquee]);

  const handleMouseUp = React.useCallback((e: React.MouseEvent) => {
    if (!marquee.isMarqueeMode || !isDraggingRef.current) return;
    isDraggingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas) {
      const additive = e.ctrlKey || e.shiftKey;
      const shown = visibleNetwork(Object.values(model.nodes), Object.values(model.spools), Object.values(model.stopes ?? {}).flat(), model);
      marquee.handleMarqueeEnd(indexBy((node) => node.id, shown.nodes), indexBy((spool) => spool.spoolId, shown.spools), additive);
    }
  }, [marquee, model.nodes, model.spools, model.stopes, model.showInactiveSpools, model.levelAreaFilter, model.spoolDateFilter]);

  React.useEffect(() => {
    if (!marquee.isMarqueeMode) {
      isDraggingRef.current = false;
    }
  }, [marquee.isMarqueeMode]);

  React.useEffect(() => {
    if (!marquee.isMarqueeMode || !overlayRef.current) return undefined;

    const overlay = overlayRef.current;
    const canvas = canvasRef.current;

    const handleWheel = (e: WheelEvent) => {
      if (!isDraggingRef.current && canvas) {
        const rect = canvas.getBoundingClientRect();
        const drawerOpen = sceneManager?.state.drawerOpen ?? false;
        const drawerWidth = drawerOpen ? MODEL_DRAW_WIDTH : 0;
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        if (x >= 0 && x <= rect.width - drawerWidth && y >= 0 && y <= rect.height) {
          const wheelEvent = new WheelEvent('wheel', {
            bubbles: true,
            cancelable: true,
            clientX: e.clientX,
            clientY: e.clientY,
            deltaX: e.deltaX,
            deltaY: e.deltaY,
            deltaZ: e.deltaZ,
            deltaMode: e.deltaMode,
          });
          canvas.dispatchEvent(wheelEvent);
          e.preventDefault();
        }
      }
    };

    const handleContextMenu = (e: MouseEvent) => {
      if (!isDraggingRef.current && canvas && e.button === 2) {
        const rect = canvas.getBoundingClientRect();
        const drawerOpen = sceneManager?.state.drawerOpen ?? false;
        const drawerWidth = drawerOpen ? MODEL_DRAW_WIDTH : 0;
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        if (x >= 0 && x <= rect.width - drawerWidth && y >= 0 && y <= rect.height) {
          const mouseEvent = new MouseEvent('mousedown', {
            bubbles: true,
            cancelable: true,
            clientX: e.clientX,
            clientY: e.clientY,
            button: 2,
          });
          canvas.dispatchEvent(mouseEvent);
        }
      }
    };

    overlay.addEventListener('wheel', handleWheel, { passive: false });
    overlay.addEventListener('contextmenu', handleContextMenu);

    return () => {
      overlay.removeEventListener('wheel', handleWheel);
      overlay.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [marquee.isMarqueeMode, sceneManager]);

  if (!marquee.isMarqueeMode) return null;

  const canvas = canvasRef.current;
  if (!canvas) return null;

  const rect = canvas.getBoundingClientRect();
  const drawerOpen = sceneManager?.state.drawerOpen ?? false;
  const drawerWidth = drawerOpen ? MODEL_DRAW_WIDTH : 0;

  const box = marquee.marqueeBox;
  const boxStyle = box ? {
    left: rect.left + Math.min(box.startX, box.endX),
    top: rect.top + Math.min(box.startY, box.endY),
    width: Math.abs(box.endX - box.startX),
    height: Math.abs(box.endY - box.startY),
  } : null;

  return (
    <>
      <Box
        ref={overlayRef}
        sx={{
          position: 'fixed',
          left: rect.left,
          top: rect.top,
          width: rect.width - drawerWidth,
          height: rect.height,
          zIndex: 1000,
          cursor: marquee.marqueeBox ? 'crosshair' : 'default',
          pointerEvents: 'auto',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      />
      {boxStyle && (
        <Box
          sx={{
            position: 'fixed',
            ...boxStyle,
            border: '2px dashed #1976d2',
            backgroundColor: 'rgba(25, 118, 210, 0.15)',
            pointerEvents: 'none',
            zIndex: 1001,
          }}
        />
      )}
    </>
  );
};

export default MarqueeOverlay;
